import { Uuid } from "@/domains/global/identifiers";
import { AppStore } from "@/store/AppStore";
import { SessionEventObservable } from "@/store/sessions/SessionEventObservable";
import { WithAppStore } from "@/store/types";
import { Dexie, liveQuery, Subscription } from "dexie";
import {
  makeObservable,
  computed,
  action,
  observable,
  runInAction,
  onBecomeObserved,
  onBecomeUnobserved,
} from "mobx";

export class SessionEventListObservable {
  private store: AppStore;
  private sessionId: Uuid;

  isLoading: boolean;
  liveQuerySubscription?: Subscription;
  subscribedSessionEventIds: Uuid[] = [];

  constructor({ sessionId, store }: { sessionId: Uuid } & WithAppStore) {
    this.store = store;
    this.sessionId = sessionId;
    this.isLoading = true;

    makeObservable<this, "store" | "sessionId">(this, {
      getAllSessionEventIdsAsync: true,
      getAllSessionEventsAsync: true,
      hasSessionEventAsync: true,
      findSessionEventAsync: true,
      store: false,
      sessionId: observable,

      isLoading: observable,
      liveQuerySubscription: observable,
      subscribedSessionEventIds: observable,
      subscribeLiveQuery: action,
      unsubscribeLiveQuery: action,

      allSessionEventIds: computed,
      allSessionEvents: computed,
      hasSessionEvent: false,
      findSessionEvent: false,
    });

    onBecomeObserved(this, "subscribedSessionEventIds", () => this.subscribeLiveQuery());
    onBecomeUnobserved(this, "subscribedSessionEventIds", () => this.unsubscribeLiveQuery());
  }

  subscribeLiveQuery() {
    this.liveQuerySubscription?.unsubscribe();
    this.liveQuerySubscription = liveQuery(() => {
      return this.store.sessionEvents.localTable
        .where("[session_id+locally_created_at+model_id]")
        .between(
          [this.sessionId, Dexie.minKey, Dexie.minKey],
          [this.sessionId, Dexie.maxKey, Dexie.maxKey]
        )
        .primaryKeys();
    }).subscribe({
      next: eventIds => {
        runInAction(() => {
          this.subscribedSessionEventIds = eventIds as unknown as Uuid[];
          this.isLoading = false;
        });
      },
    });
  }

  unsubscribeLiveQuery() {
    this.liveQuerySubscription?.unsubscribe();
  }

  get allSessionEventIds(): Set<Uuid> {
    return new Set(this.subscribedSessionEventIds);
  }

  async getAllSessionEventIdsAsync(): Promise<Uuid[]> {
    const eventIds = await this.store.sessionEvents.localTable
      .where("[session_id+locally_created_at+model_id]")
      .between(
        [this.sessionId, Dexie.minKey, Dexie.minKey],
        [this.sessionId, Dexie.maxKey, Dexie.maxKey]
      )
      .primaryKeys();
    return eventIds as unknown as Uuid[];
  }

  get allSessionEvents(): SessionEventObservable[] {
    return Array.from(this.allSessionEventIds)
      .map(id => this.store.sessionEvents.get(id))
      .filter((event): event is SessionEventObservable => !!event)
      .sort((a, b) => a.locallyCreatedAt.getTime() - b.locallyCreatedAt.getTime());
  }

  async getAllSessionEventsAsync(): Promise<SessionEventObservable[]> {
    const allEventIds = await this.getAllSessionEventIdsAsync();
    const allEvents = await Promise.all(
      allEventIds.map(id => this.store.sessionEvents.getAsync(id))
    );
    return allEvents
      .filter((event): event is SessionEventObservable => !!event)
      .sort((a, b) => a.locallyCreatedAt.getTime() - b.locallyCreatedAt.getTime());
  }

  public hasSessionEvent({ eventId }: { eventId: Uuid }) {
    return this.allSessionEventIds.has(eventId);
  }

  public async hasSessionEventAsync({ eventId }: { eventId: Uuid }) {
    const allEventIds = await this.getAllSessionEventIdsAsync();
    return allEventIds.includes(eventId);
  }

  public findSessionEvent({ eventId }: { eventId: Uuid }) {
    return this.allSessionEventIds.has(eventId) ? this.store.sessionEvents.get(eventId) : undefined;
  }

  public async findSessionEventAsync({ eventId }: { eventId: Uuid }) {
    const event = await this.store.sessionEvents.getAsync(eventId);
    if (!event || event.sessionId !== this.sessionId) {
      return undefined;
    }
    return event;
  }
}
